<template>
  <v-row>
    <v-col>
      <v-data-table
        :headers="headers"
        :items="captures"
        :server-items-length="totalCaptures"
        :loading="capturesLoading"
        hide-default-footer
        :multi-sort="false"
        :must-sort="true"
        @page-count="pageCount = $event"
        :page.sync="page"
        :options.sync="options"
        :items-per-page="itemsPerPage"
        :footer-props="{ showFirstLastPage: true }"
        class="ic-table elevation-0 ic-table--heavy-headers"
      >
        <template v-slot:top>
          <v-container fluid class="white header-container">
            <v-row align="center" dense justify="center">
              <v-col cols="12" md="2" class="v-toolbar__title">
                {{ $t("pages.capture.list.title") }}
              </v-col>
              <v-col cols="12" md="10">
                <div class="d-flex flex-wrap justify-end toolbar-items">
                  <v-text-field
                    class="toolbar-item"
                    v-model="dynamicSearch"
                    append-icon="$icons_system_search"
                    :label="$t('misc.search')"
                    single-line
                    clearable
                    dense
                    outlined
                    hide-details
                    style="max-width: 184px;"
                  ></v-text-field>
                  <v-select class="date-type-picker toolbar-item"
                            :items="dateTypes"
                            hide-details
                            outlined
                            dense
                            value="captureDate"
                            v-model="dateType"
                            style="max-width: 184px;"
                  ></v-select>
                  <div class="toolbar-item capture-date-picker" style="max-width: 284px;">
                    <v-icon class="date-picker-icon-calendar">$icons_product_calendar</v-icon>
                    <date-picker
                      :label="$t('pages.capture.list.startDate')"
                      :locale="selectedLanguage"
                      :displayFormat="$t('pages.capture.list.dateFormat')"
                      v-model="startDate"
                      :max="endDate"
                    ></date-picker>
                    <v-icon class="date-picker-icon-arrow">$icons_system_arrow</v-icon>
                    <date-picker
                      :label="$t('pages.capture.list.endDate')"
                      :locale="selectedLanguage"
                      :displayFormat="$t('pages.capture.list.dateFormat')"
                      v-model="endDate"
                      :min="startDate"
                    ></date-picker>
                  </div>
                  <v-btn
                    color="primary"
                    :loading="reportGenerating"
                    @click="downloadReport()"
                    class="toolbar-item header-list-button"
                  >
                    <span>{{ $t("pages.capture.list.download") }}</span>
                  </v-btn>
                </div>
              </v-col>
            </v-row>
          </v-container>
          <div class="header-separator"></div>
        </template>
        <template v-slot:header.captureDate="{ header }">
          <span class="custom-table-header">{{ header.text }}</span>
        </template>
        <template v-slot:header.payout.arrivalDate="{ header }">
          <span class="custom-table-header">{{ header.text }}</span>
        </template>
        <template v-slot:header.card.brand="{ header }">
          <span class="custom-table-header">{{ header.text }}</span>
        </template>
        <template v-slot:header.booking.dispatchId="{ header }">
          <span class="custom-table-header">{{ header.text }}</span>
        </template>
        <template v-slot:header.applicationFee="{ header }">
          <span class="custom-table-header">{{ header.text }}</span>
        </template>

        <template v-slot:header.netAmount="{ header }">
          <span class="custom-table-header">{{ header.text }}</span>
        </template>

        <template v-slot:header.booking.amount="{ header }">
          <span class="custom-table-header">{{ header.text }}</span>
        </template>
        <template v-slot:item.captureDate="{ item }">
          <span>{{ formatDate(item.captureDate) }}</span>
        </template>
        <template v-slot:item.payout.arrivalDate="{ item }">
          <span>{{ formatDate(item.payout && item.payout.arrivalDate) }}</span>
        </template>

        <template v-slot:item.card.brand="{ item }">
              <span>
                <v-icon>{{ creditCardIcon(item.card.brand) }}</v-icon>
              </span>
        </template>

        <template v-slot:item.netAmount="{ item }">
          <template v-if="item.payoutAmount !== null">
            {{ item.payoutAmount }}
          </template>
          <template v-else>
            {{ item.netAmount }}
          </template>
        </template>

      </v-data-table>
      <div class="text-center pt-8">
        <v-pagination v-model="page" :length="pageCount" total-visible="10"></v-pagination>
      </div>
    </v-col>
  </v-row>
</template>

<script>
import moment from 'moment';
import debounce from 'debounce';
import dayjs from '../plugins/dayjs';
import DatePicker from './DatePicker.vue';
import { creditCardIcon } from '../mixins/creditCardIcons.mixins';

export default {
  name: 'captureList',
  components: { DatePicker },
  mixins: [
    creditCardIcon,
  ],
  props: {
    capturesLoading: {
      type: Boolean,
      required: true,
    },
    loadingCharge: {
      type: Boolean,
      required: true,
    },
    captures: {
      type: Array,
      required: true,
    },
    totalCaptures: {
      type: Number,
      required: true,
    },
    selectedLanguage: {
      type: String,
      required: true,
    },
    reportGenerating: {
      type: Boolean,
      required: true,
    },
  },
  created() {
    this.debouncedLoad = debounce(this.load, 500);
  },
  data() {
    return {
      screenName: 'capture-list',
      options: {
        sortBy: ['captureDate'], sortDesc: [true],
      },
      dialogs: {},
      page: 1,
      pageCount: 0,
      itemsPerPage: 10,
      loading: false,
      dateType: 'captureDate',
      startDate: dayjs()
        .subtract(31, 'days')
        .format('YYYY-MM-DD'),
      endDate: dayjs().format('YYYY-MM-DD'),
      dynamicSearch: '',
    };
  },
  watch: {
    options: {
      handler() {
        this.load();
      },
      deep: true,
    },
    totalCaptures() {
      this.options.pageCount = Math.round(this.totalCaptures / this.itemsPerPage) + 1;
    },
    startDate() {
      this.load();
    },
    endDate() {
      this.load();
    },
    dateType() {
      this.load();
    },
    dynamicSearch() {
      this.page = 1;
      this.debouncedLoad();
    },
  },
  computed: {
    dateTypes() {
      return [
        { text: this.$t('pages.capture.filters.captureDate'), value: 'captureDate' },
        { text: this.$t('pages.capture.filters.payoutDate'), value: 'payoutDate' },
      ];
    },
    startDateForRequest() {
      return `${this.startDate}T00:00:00Z`;
    },
    endDateForRequest() {
      return `${this.endDate}T23:59:59Z`;
    },
    headers() {
      return [
        {
          text: this.$t('pages.dispute.list.bookingChannelName'),
          align: 'left',
          sortable: true,
          value: 'booking.bookingChannelName',
        },
        {
          text: this.$t('pages.capture.list.captureDate'),
          align: 'left',
          sortable: true,
          value: 'captureDate',
        },
        {
          text: this.$t('pages.capture.list.bookingId'),
          align: 'left',
          sortable: true,
          value: 'booking.dispatchId',
        },
        {
          text: this.$t('pages.capture.list.cardBrand'),
          align: 'left',
          sortable: true,
          value: 'card.brand',
        },
        {
          text: this.$t('pages.capture.list.fareAmount'),
          align: 'left',
          sortable: true,
          value: 'booking.amount',
        },
        {
          text: this.$t('pages.capture.list.netAmount'),
          align: 'left',
          sortable: true,
          value: 'netAmount',
        },
        {
          text: this.$t('pages.capture.list.payoutDate'),
          align: 'left',
          sortable: true,
          value: 'payout.arrivalDate',
        },
      ];
    },
  },
  methods: {
    async load() {
      if (moment(this.endDate).diff(moment(this.startDate), 'days') > 31) {
        this.$store.dispatch('globalMessages/addError',
          { message: 'lists.dateRangeError' });
        return;
      }
      const { page, itemsPerPage, sortBy, sortDesc } = this.options;
      const sort = sortBy[0] || 'captureDate';
      this.$emit('loadData', {
        page: page - 1,
        startDate: this.startDateForRequest,
        endDate: this.endDateForRequest,
        dateType: this.dateType,
        maxPerPage: itemsPerPage,
        sort: sortDesc[0] ? `-${sort}` : sort,
        dynamicSearch: this.dynamicSearch,
      });
    },
    downloadReport() {
      const { sortBy, sortDesc } = this.options;
      const sort = sortBy[0] || 'captureDate';
      this.$emit('downloadReport', {
        startDate: this.startDateForRequest,
        endDate: this.endDateForRequest,
        dateType: this.dateType,
        sort: sortDesc[0] ? `-${sort}` : sort,
        dynamicSearch: this.dynamicSearch,
      });
    },
    formatDate(date) {
      if (!date) return null;
      return dayjs(date).utc().format('YYYY-MM-DD HH:mm');
    },
  },
};
</script>

<style lang="scss">
.toolbar-items {
  .toolbar-item:not(:first-child) {
    margin-left: 8px;
    margin-bottom: 8px;
  }
}

.capture-date-picker {
  display: flex;
  flex-flow: row;
  border: 1px #a1a1a1 solid;
  border-radius: 4px;
  padding: 4px 12px;
  max-height: 40px;
  align-items: center;
}

.mobile-title-capture {
  display: flex;
  justify-content: space-between;

  .header-list-button {
    padding: 0 !important;
    min-width: 45px !important;
  }
}

.capture-header-menu {
  display: flex;
  flex-flow: row;
  align-items: center;
}

.date-picker-icon-arrow {
  min-width: 20px;
  min-height: 20px;
}

.date-picker-icon-calendar {
  width: 30px;
  height: 30px;
  min-width: 20px;
  min-height: 20px;
}

@media only screen and (max-width: 1273px) {
  .date-picker-icon-calendar {
    min-width: 20px;
    min-height: 20px;
    width: 20px;
    height: 20px;
  }
}

@media only screen and (max-width: 959px) {
  .capture-header-menu-input {
    padding-top: 10px !important;
    padding-bottom: 10px !important;
  }
}
</style>
